import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { OvertimeType } from '@/types/timesheetSettings';
import { OvertimeTypeBadge } from './OvertimeTypeBadge';

// ─── OvertimeTypeCell ────────────────────────────────────────────────────────
// Extracted as a named sub-component to avoid complex nested ternaries in JSX
// that can cause Babel/Vite parse errors.

export interface OvertimeTypeCellProps {
  entry: { overtimeTypeId?: number; overtimeTypeName?: string };
  overtimeTypes: OvertimeType[];
  isReadOnly: boolean;
  isAbsence: boolean;
  onChangeOvertimeType: (typeId: number | undefined) => void;
}

export function OvertimeTypeCell({ entry, overtimeTypes, isReadOnly, isAbsence, onChangeOvertimeType }: OvertimeTypeCellProps) {
  if (isAbsence) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  const selectedType = overtimeTypes.find(t => t.id === entry.overtimeTypeId);

  if (isReadOnly) {
    if (selectedType) {
      return <OvertimeTypeBadge name={selectedType.name} colour={selectedType.badgeColour} />;
    }
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  return (
    <Select
      value={entry.overtimeTypeId != null ? String(entry.overtimeTypeId) : 'none'}
      onValueChange={(val) => onChangeOvertimeType(val === 'none' ? undefined : Number(val))}
    >
      <SelectTrigger className="h-9 text-xs min-w-0">
        {selectedType ? (
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className="h-2.5 w-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: selectedType.badgeColour }}
            />
            <span className="truncate">{selectedType.name}</span>
          </div>
        ) : (
          <SelectValue placeholder="Standard" />
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">
          <span className="text-muted-foreground">Standard</span>
        </SelectItem>
        {overtimeTypes.map(ot => (
          <SelectItem key={ot.id} value={String(ot.id)}>
            <div className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: ot.badgeColour }}
              />
              <span>{ot.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
